import PaytoIcon from 'assets/icons/payments/payto-icon-2.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import TooltipCommon from 'components/common/TooltipCommon';
import {
  DESKTOP_MIN_WIDTH,
  FUNC_DEFAULT,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import withAddPaytoAccountsAgreement from 'features/to/HOCs/payment/withAddPaytoAccountsAgreement';
import withCheckMaxAmount from 'features/to/HOCs/payment/withCheckMaxAmount';
import withDeletePaytoAccountAgreements from 'features/to/HOCs/payment/withDeletePaytoAccountAgreements';
import withRoundUpPaytoMaxAmount from 'features/to/HOCs/payment/withRoundUpPaytoMaxAmount';
import CustomMaxAmountDropdown from 'features/to/components/payment/payto/CustomMaxAmountDropdown';
import FormInputAmountValidation from 'features/to/components/payment/payto/FormInputAmountValidation';
import FormPayidAccountDetails from 'features/to/components/payment/payto/FormPayidAccountDetails';
import FormPaytoAccountDetails from 'features/to/components/payment/payto/FormPaytoAccountDetails';
import {
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BANK_NAME_NAME,
  FORM_ADD_PAYTO_BSB_NAME,
  FORM_ADD_PAYTO_MAX_AMOUNT_FIELD,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
  FORM_ADD_PAYTO_PAYID_TYPE_NAME,
  isPayToLimitNullEquivalent,
  MAX_AMOUNT_OPTION_DATA,
  PAYTO_LOG_TAG,
} from 'features/to/const';
import usePaytoAPI from 'features/to/hooks/payment/usePaytoAPI';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexColCenterCenterMixin,
  displayFlexRowCenterEndMixinObject,
  fontSize12MixinObject,
  fontSize14MixinObject,
  fontSize16MixinObject,
  paragraph500Mixin,
  paragraphMixin,
} from 'styles';

const FormInputMaxAmountValidationRoundUp = withRoundUpPaytoMaxAmount(
  FormInputAmountValidation,
);
const FormSelectionMaxAmountValidation = withCheckMaxAmount(
  CustomMaxAmountDropdown,
);

const Popup = withRoundUpPaytoMaxAmount(
  withAddPaytoAccountsAgreement(
    withDeletePaytoAccountAgreements(
      ({
        isOpen,
        buttonLabel1,
        onClose,
        buttonLabel2,
        onClick,
        isBackdropOverlap,
        zIndexBackdropOverlap,
        zIndexModal = 0,
        onUpdatePaytoAccountAuthorise,
        onShowAuthorisePaytoAccountModal,
        onOpenPaytoErrorCodeModal,
        modalOptions,
        accountAgreement,
        bsbNumberMaskStr,
        setBsbNumberMaskStr,
        accountNumberMaskStr,
        setAccountNumberMaskStr,
        numberMaskStr,
        setNumberMaskStr,
        getAmountRoundUp,
        totalToPay,
        maxAmountInitialVer2,
        onDelete,
        onEdit,
        getAmountRoundUpVer2,
        onSelect,
      }) => {
        //   const {
        //     "paymentAgreementUID" = "EAPAYTODWY75E5RJQP1LO0M1759212580",
        //     "paymentAgreementStatus": "Active",
        //     "accountNumber": "*********100",
        //     "bsb": "802950",
        //     "bankCode": null,
        //     "bankName": null,
        //     "maxAmount": 2000,
        //     "responseBy": "10/05/2025 16:09:40",
        //     "pendingAmend": false,
        //     "blocked": true,
        //     "payId": "04******01"
        // } = accountAgreement || {};

        const { t } = useLang();
        const { isDesktop } = useDetectDevice();

        const {
          resetPaytoAgreementEditing,
          resetPaytoMaxAmountEdited,
          setPaytoAccountAgreementEditing,
          setPaytoMaxAmountEdited,
          paytoAccountInformation,
          isPaytoMaxAmountEdited,
          paytoAccountsAgreement,
        } = usePaytoStore();

        const {
          payloadTransformFromPaytoAccountAgreement,
          payloadTransformFromPaytoAccountInformation,
        } = usePaytoAPI();

        const [accountAgreementInformation, setAccountAgreementInformation] =
          useState(
            payloadTransformFromPaytoAccountAgreement({
              ...accountAgreement,
              maxAmount: getAmountRoundUpVer2(accountAgreement?.maxAmount),
            }),
          );
        const [accountAgreementValidation, setAccountAgreementValidation] =
          useState();

        const isEditAgreementByBsb = !accountAgreement?.payId;

        const isCurrentSelected =
          paytoAccountInformation?.id === accountAgreementInformation?.id;

        // DEV-1401 Corrected Amend precedence (highest to lowest, first
        // match wins). amendPayToLimit comes straight from accountAgreement
        // (per-agreement, via payloadTransformFromPaytoAccountAgreement) --
        // never createPayToLimit, which is a Create-flow-only concept and is
        // deliberately excluded here (former row #10, removed from scope).
        const amendPayToLimit = accountAgreementInformation?.amendPayToLimit;
        const isPositiveAmendPayToLimit =
          !isPayToLimitNullEquivalent(amendPayToLimit) &&
          Number(amendPayToLimit) > 0;

        // Rule 1 (HARD-STOP): current (unedited) maxAmount > positive
        // amendPayToLimit, AND amendPayToLimit <= 1000. Uses the raw
        // accountAgreement prop, not the editable accountAgreementInformation
        // state, so a user's in-progress dropdown edit never re-derives this
        // -- it's a fixed property of the agreement as it stands today.
        // Client-confirmed 2026-09-11: amendPayToLimit > 1000 must NEVER
        // hard-stop, even when currentMaxAmount > amendPayToLimit would have
        // triggered the old unconditional rule -- see
        // isPositiveLowAmendPayToLimit below.
        const currentMaxAmount = Number(accountAgreement?.maxAmount);
        const isPositiveLowAmendPayToLimit =
          isPositiveAmendPayToLimit && Number(amendPayToLimit) <= 1000;
        const isAmendHardStop =
          isPositiveLowAmendPayToLimit && currentMaxAmount > amendPayToLimit;

        // DEV-1401: hides Continue/the popup note whenever totalPay exceeds
        // BOTH the agreement's raw currentMaxAmount AND amendPayToLimit --
        // independent of isAmendHardStop (which is totalToPay-agnostic) and
        // of isAmendBankLimitValidation (which is keyed to the currently
        // SELECTED dropdown value, not currentMaxAmount, so it can be true
        // here while this is false, e.g. a lower selection still under
        // currentMaxAmount). OR'd into the existing isAmendHardStop gates
        // below rather than replacing them, so already-hidden hard-stop
        // rows stay hidden regardless of totalToPay.
        const isAmendTotalOverBothLimits =
          isPositiveAmendPayToLimit &&
          Number(totalToPay) > currentMaxAmount &&
          Number(totalToPay) > Number(amendPayToLimit);
        const isAmendHideContinueAndNote =
          isAmendHardStop || isAmendTotalOverBothLimits;

        // DEV-1401 (client requirement: Update#170926#2 item 2, narrowed to the
        // hard-stop family by the amendPayToLimit <= 1000 rule). A hard-stop row
        // whose totalPay also exceeds BOTH currentMaxAmount and amendPayToLimit
        // shows the bank-limit message instead of the shared uneditable one.
        //
        // isAmendHardStop is conjoined for SCOPING, not to correct the message:
        // an amendPayToLimit > 1000 row already reaches the same
        // form_payto_bank_limit_cannot_pay_validation via
        // isAmendBankLimitValidation in withCheckMaxAmount, once the reset
        // effect narrows its selection. Without this conjunct that row would
        // also emit the key from here on its pre-reset mount frame (e.g.
        // amendPayToLimit=2000, currentMaxAmount=1500, totalToPay=3000:
        // getAmountRoundUpVer2 rounds the initial selection up to 3000 -- in
        // either branch, so independent of isAccountAgreementEditing -- so
        // selectedMaxAmount is not yet below totalToPay and
        // isAmendBankLimitValidation is not yet true). That would change
        // behaviour outside the requested amendPayToLimit <= 1000 scope.
        //
        // Declare after BOTH operands. Above isAmendHardStop this is an
        // unconditional TDZ ReferenceError; BETWEEN the two declarations it
        // throws only when isAmendHardStop is truthy, because && short-circuits
        // -- i.e. it would pass every non-hard-stop row and fail exactly the
        // rows this feature targets.
        // Also drives AmendUneditableWarning's $isError at the render site
        // below -- this boolean and the message ternary must stay in sync.
        const isAmendHardStopBankLimit =
          isAmendHardStop && isAmendTotalOverBothLimits;

        // DEV-1401 (2026-09-15 revision): client reversed the 2026-09-11
        // totalPay-scoping above -- every isAmendHardStop row (regardless of
        // totalToPay) now shows the agreement's real currentMaxAmount, both
        // as the displayed value and as the dropdown's sole selectable
        // option, instead of a forced 1000. That's exactly isAmendHardStop's
        // own condition, so this no longer needs a second, separately-typed
        // boolean -- isAmendHardStop is used directly below and at the
        // display-value ternary and the reset-effect deps.

        // Rule 2 (UNEDITABLE, not hard-stop), client-revised 2026-09-11:
        // amendPayToLimit <= 1000 (and not hard-stop) forces the option list
        // to the single 1000 entry, uneditable, with the existing warning.
        // amendPayToLimit > 1000 uses the existing filter-by-limit result
        // and stays OUT of the single-option rule even if filtering happens
        // to leave exactly one option -- filtered count alone must never
        // force uneditable for amendPayToLimit > 1000. totalPay-uneditable
        // is unchanged and independently enforced inside withCheckMaxAmount's
        // onCheckMaxAmountValidation too; recomputed here only to drive this
        // modal's own footer/disable decisions, not as a second gate.
        //
        // DEV-1401: isAmendHardStop is checked first so that sub-case renders
        // a synthetic single option built directly from currentMaxAmount (not
        // necessarily one of the 6 static presets) instead of the forced-1000
        // entry every other isPositiveLowAmendPayToLimit row still uses.
        const amendMaxAmountOptions = isAmendHardStop
          ? [
              {
                key: `AUD ${currentMaxAmount}`,
                value: String(currentMaxAmount),
              },
            ]
          : isPositiveLowAmendPayToLimit
            ? [MAX_AMOUNT_OPTION_DATA[0]]
            : isPositiveAmendPayToLimit
              ? MAX_AMOUNT_OPTION_DATA.filter(
                  (option) => Number(option?.value) <= Number(amendPayToLimit),
                )
              : MAX_AMOUNT_OPTION_DATA;
        const isAmendSingleOptionUneditable =
          isPositiveLowAmendPayToLimit && !isAmendHardStop;
        const isAmendTotalPayUneditable =
          isPositiveAmendPayToLimit &&
          Number(totalToPay) > Number(amendPayToLimit);
        const isAmendUneditable =
          isAmendSingleOptionUneditable || isAmendTotalPayUneditable;

        // DEV-1401 Task 2A/2B (2026-09-17 revision): reclassified from the
        // agreement's fixed currentMaxAmount to the CURRENTLY SELECTED
        // dropdown value -- the actual symptom is "selected can't cover
        // totalPay"; whether a higher selectable option (up to
        // amendPayToLimit) could still cover totalPay is what decides which
        // of the two messages applies. Client-confirmed: only the message
        // text differs between 2A and 2B -- editable/disabled state and the
        // synthetic currentMaxAmount display bypass below are unchanged.
        const selectedMaxAmount = Number(
          accountAgreementInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME],
        );
        const isAmendSelectedBelowTotalPay =
          isPositiveAmendPayToLimit &&
          !isPositiveLowAmendPayToLimit &&
          selectedMaxAmount < Number(totalToPay);
        // Task 2A: a higher selectable option (<= amendPayToLimit) could
        // still cover totalPay.
        const isAmendLimitValidation =
          isAmendSelectedBelowTotalPay &&
          Number(totalToPay) <= Number(amendPayToLimit);
        // Task 2B: totalPay exceeds amendPayToLimit itself -- no selectable
        // option can cover it. Supersedes the old currentMaxAmount-anchored
        // "partial limit" case, which was a strict subset of this condition.
        const isAmendBankLimitValidation =
          isAmendSelectedBelowTotalPay &&
          Number(totalToPay) > Number(amendPayToLimit);
        const isAmendLimitFamilyValidation =
          isAmendLimitValidation || isAmendBankLimitValidation;

        // New 2026-09-11: amendMaxAmountOptions can now be a narrower list
        // than the full static 6 entries (forced to 1000, or filtered by
        // amendPayToLimit > 1000), so a previously-selected maxAmount value
        // that's no longer a member must be reset -- otherwise
        // CustomMaxAmountDropdown's hardcoded data[5] fallback resolves to
        // undefined for a shorter array and crashes on render. Depends on
        // the primitive drivers, not the amendMaxAmountOptions array
        // reference itself, since that's freshly allocated every render.
        useEffect(() => {
          if (isEmpty(amendMaxAmountOptions)) return;
          const currentValue =
            accountAgreementInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME];
          const isValid = amendMaxAmountOptions.some(
            (option) => Number(option?.value) === Number(currentValue),
          );
          if (!isValid) {
            const fallback =
              amendMaxAmountOptions[amendMaxAmountOptions.length - 1];
            setAccountAgreementInformation((prev) => ({
              ...prev,
              [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: fallback?.value,
            }));
          }
          // DEV-1401: isAmendHardStop drives amendMaxAmountOptions' synthetic
          // currentMaxAmount option above, so this reset effect must also
          // re-run on that transition; currentMaxAmount itself is derived
          // from accountAgreement, not from a state setter, so it isn't a
          // dependency here.
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [amendPayToLimit, isPositiveLowAmendPayToLimit, isAmendHardStop]);

        // Single UI trigger for both the maxAmount dropdown's disabled state
        // and the shared warning message -- rule 1 and rule 2 both resolve
        // to the SAME message written into the SAME validation slot the
        // dropdown already reads its error from, so there is exactly one
        // render location and no risk of duplicating the message when both
        // rules are true at once (rule 1's footer already wins in that
        // case).
        // DEV-1401 Task 2A/2B: isAmendLimitFamilyValidation is excluded here
        // so that row becomes editable (with its own inline keyError
        // message, wired via withCheckMaxAmount below) instead of
        // hard-disabled.
        const isAmendMaxAmountDisabled =
          (isAmendHardStop || isAmendUneditable) &&
          !isAmendLimitFamilyValidation;

        // DEV-1401: this is now the SOLE trigger for the shared warning
        // message, passed to withCheckMaxAmount as amendForcedValidationMessage
        // (see that HOC for why). A previous version wrote this message via
        // its own separate useEffect alongside withCheckMaxAmount's own
        // internal effect writing the same accountAgreementValidation slot --
        // two effects racing on one field, with no branch clearing the
        // message when neither rule applied. Passing this as a prop instead
        // gives withCheckMaxAmount everything it needs to own the field's
        // full lifecycle (set AND clear) in one place.
        // DEV-1401 Task 2A/2B: excluded identically to isAmendMaxAmountDisabled
        // above -- for isAmendLimitFamilyValidation this must be falsy both
        // so the field isn't force-disabled and so CustomMaxAmountDropdown's
        // keyError (amendForcedValidationMessage ? '' : validation[field])
        // falls through to the inline validation message withCheckMaxAmount
        // writes for that case instead of being suppressed.
        const amendForcedValidationMessage =
          (isAmendHardStop || isAmendUneditable) &&
          !isAmendLimitFamilyValidation
            ? t(
                isAmendHardStopBankLimit
                  ? 'form_payto_bank_limit_cannot_pay_validation'
                  : 'label_payto_amend_unable_increase_limit_per_transaction_desc',
              )
            : undefined;

        const handleOnSubmit = async () => {
          try {
            let newAccountAgreementInformation = {
              ...accountAgreementInformation,
            };
            let newAccountAgreementValidation = {
              ...accountAgreementValidation,
            };

            if (!isEditAgreementByBsb) {
              delete newAccountAgreementInformation[FORM_ADD_PAYTO_BSB_NAME];
              delete newAccountAgreementValidation[FORM_ADD_PAYTO_BSB_NAME];
              delete newAccountAgreementInformation[
                FORM_ADD_PAYTO_BANK_NAME_NAME
              ];
              delete newAccountAgreementValidation[
                FORM_ADD_PAYTO_BANK_NAME_NAME
              ];
              delete newAccountAgreementInformation[
                FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME
              ];
              delete newAccountAgreementValidation[
                FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME
              ];
              delete newAccountAgreementInformation?.bankCode;
              delete newAccountAgreementValidation?.bankCode;
            }

            if (isEditAgreementByBsb) {
              if (
                isEmpty(
                  newAccountAgreementInformation?.[
                    FORM_ADD_PAYTO_BANK_NAME_NAME
                  ],
                )
              ) {
                delete newAccountAgreementInformation?.[
                  FORM_ADD_PAYTO_BANK_NAME_NAME
                ];
                delete newAccountAgreementValidation?.[
                  FORM_ADD_PAYTO_BANK_NAME_NAME
                ];
              }
              delete newAccountAgreementInformation[
                FORM_ADD_PAYTO_PAYID_TYPE_NAME
              ];
              delete newAccountAgreementValidation[
                FORM_ADD_PAYTO_PAYID_TYPE_NAME
              ];
              delete newAccountAgreementInformation[
                FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
              ];
              delete newAccountAgreementValidation[
                FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
              ];
            }

            if (
              isPayToLimitNullEquivalent(
                newAccountAgreementInformation?.amendPayToLimit,
              )
            ) {
              delete newAccountAgreementInformation?.amendPayToLimit;
            }

            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 handleOnSubmit ➡️ newAccountAgreementInformation ↩`,
              newAccountAgreementInformation,
            );
            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 handleOnSubmit ➡️ newAccountAgreementValidation ↩`,
              newAccountAgreementValidation,
            );

            const isAllowSubmit =
              Object.values(newAccountAgreementInformation).every(
                (value) => value,
              ) &&
              Object.values(newAccountAgreementValidation).every(
                (value) => !value,
              );
            if (isAllowSubmit) {
              const currentAccountAgreementUpdating =
                paytoAccountsAgreement?.find(
                  (account) =>
                    account?.paymentAgreementUID ===
                    accountAgreement?.paymentAgreementUID,
                );
              console.log(
                `⭐ EditPaytoAccountAgreementPopup 👉 isAllowSubmit ➡️ currentAccountAgreementUpdating ↩`,
                currentAccountAgreementUpdating,
              );
              console.log(
                `⭐ EditPaytoAccountAgreementPopup 👉 isAllowSubmit ➡️ accountAgreement ↩`,
                accountAgreement,
              );
              console.log(
                `⭐ EditPaytoAccountAgreementPopup 👉 isAllowSubmit ➡️ accountAgreementInformation ↩`,
                accountAgreementInformation,
              );
              console.log(
                `⭐ EditPaytoAccountAgreementPopup 👉 isAllowSubmit ➡️ paytoAccountInformation ↩`,
                paytoAccountInformation,
              );
              if (
                Number(currentAccountAgreementUpdating?.maxAmount) ===
                Number(
                  accountAgreementInformation[FORM_ADD_PAYTO_MAX_AMOUNT_NAME],
                )
              ) {
                onOpenPaytoErrorCodeModal(
                  { errorCode: '6666' },
                  '',
                  t('popup_payto_no_change'),
                  FUNC_DEFAULT,
                  '',
                  {
                    isHideTitle: true,
                  },
                );
                return;
              }

              if (isEditAgreementByBsb) {
                console.log(
                  `⭐ ${PAYTO_LOG_TAG} 👉 isAllowSubmit ➡️ accountAgreementInformation ➡️ isEditAgreementByBsb ↩`,
                  accountAgreementInformation,
                );
              }
              if (!isEditAgreementByBsb) {
                console.log(
                  `⭐ ${PAYTO_LOG_TAG} 👉 isAllowSubmit ➡️ accountAgreementInformation ➡️ NO isEditAgreementByBsb ↩`,
                  accountAgreementInformation,
                );
              }

              onUpdatePaytoAccountAuthorise(
                payloadTransformFromPaytoAccountInformation(
                  newAccountAgreementInformation,
                ),
                () => {
                  onClose();
                  onShowAuthorisePaytoAccountModal({
                    isEdit: true,
                  });
                },
                onOpenPaytoErrorCodeModal,
              );
            }
          } catch (error) {
            console.error(
              `🔥 ${PAYTO_LOG_TAG} ❌ handleOnSubmit ➡️ ERROR ↩`,
              error?.message,
            );
          }
        };

        const handleOnClose = () => {
          resetPaytoAgreementEditing();
          resetPaytoMaxAmountEdited();
          setPaytoMaxAmountEdited();
          onClose();
          if (isCurrentSelected) {
            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 handleOnClose ➡️ isCurrentSelected ↩`,
              accountAgreement,
            );
            onSelect(accountAgreement);
          } else {
            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 handleOnClose ➡️ NOT isCurrentSelected ↩`,
              payloadTransformFromPaytoAccountInformation(
                paytoAccountInformation,
              ),
            );
            onSelect(
              payloadTransformFromPaytoAccountInformation(
                paytoAccountInformation,
              ),
            );
          }
        };

        const handleOnDelete = () => {
          onDelete(
            accountAgreement,
            () => {},
            () => {
              handleOnClose();
            },
          );
        };

        const handleSelectionMaxAmountOnChange = () => {
          setPaytoMaxAmountEdited(true);
        };

        const renderPaytoBankAccountsVerified = () => {
          return (
            <>
              <SizeBox height={34} />
              <FormPaytoAccountDetails
                paytoAccountInformation={accountAgreementInformation}
                setPaytoAccountInformation={setAccountAgreementInformation}
                paytoAccountValidation={accountAgreementValidation}
                setPaytoAccountValidation={setAccountAgreementValidation}
                bsbNumberMaskStr={bsbNumberMaskStr}
                setBsbNumberMaskStr={setBsbNumberMaskStr}
                accountNumberMaskStr={accountNumberMaskStr}
                setAccountNumberMaskStr={setAccountNumberMaskStr}
              />
            </>
          );
        };
        const renderPaytoPayIdAccountsVerified = () => {
          return (
            <>
              <SizeBox height={34} />
              <FormPayidAccountDetails
                paytoAccountInformation={accountAgreementInformation}
                setPaytoAccountInformation={setAccountAgreementInformation}
                paytoAccountValidation={accountAgreementValidation}
                setPaytoAccountValidation={setAccountAgreementValidation}
                numberMaskStr={numberMaskStr}
                setNumberMaskStr={setNumberMaskStr}
              />
            </>
          );
        };
        const renderAccountAgreementInfo = () => {
          if (isEditAgreementByBsb)
            return <>{renderPaytoBankAccountsVerified()}</>;
          return <>{renderPaytoPayIdAccountsVerified()}</>;
        };

        return (
          <>
            <style>
              {zIndexModal &&
                `
          @media screen and (max-width: ${TABLET_MAX_WIDTH - 1}px) {
            .modal {
              z-index: ${zIndexModal || '1101'};
            }
          }
        `}
              {isBackdropOverlap
                ? `
          .add-payto-account-backdrop {
            z-index: ${zIndexBackdropOverlap || '1100'};
            background: #212121;
            opacity: 0.7!important;
            display: initial!important;
          }
        `
                : `
        .add-payto-account-backdrop {
          background: #212121;
          opacity: 0.7!important;
          display: initial!important;
        }
      `}
            </style>
            <ModalStyled
              show={isOpen}
              onHide={() => {}}
              aria-labelledby="contained-modal-title-vcenter"
              backdropClassName="add-payto-account-backdrop"
              centered
            >
              <ModalBody>
                <BodyContentTop>
                  <img src={PaytoIcon} height={40} />
                  <SizeBox height={20} />
                  <Desc>{t('label_edit_payto_account_popup_desc')}</Desc>
                </BodyContentTop>
                <SizeBox height={20} />
                <BodyContentSection>
                  <Label>{t('label_edit_payto_account_popup_2')}</Label>{' '}
                  <TooltipCommon
                    field={FORM_ADD_PAYTO_MAX_AMOUNT_FIELD}
                    text={`${t('label_max_amount_tooltip')}`}
                    buttonStyles={{
                      marginLeft: '0',
                    }}
                    isUseImage
                  />
                  <SizeBox height={4} />
                  <SectionDesc style={fontSize16MixinObject}>
                    <FormSelectionMaxAmountValidation
                      field={FORM_ADD_PAYTO_MAX_AMOUNT_FIELD}
                      data={amendMaxAmountOptions}
                      keyFind="key"
                      keyProp="value"
                      dropdownItemPrefix="AUD"
                      information={{
                        ...accountAgreementInformation,
                        [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: isPaytoMaxAmountEdited
                          ? `${accountAgreementInformation[FORM_ADD_PAYTO_MAX_AMOUNT_NAME]}`
                          : // DEV-1401: bypass getAmountRoundUpVer2 for the
                            // hard-stop row, which must display the
                            // agreement's actual currentMaxAmount -- that
                            // helper rounds UP to the nearest static preset
                            // from the full, unfiltered option list whenever
                            // totalToPay > maxAmount, which is not
                            // guaranteed to equal currentMaxAmount. The
                            // isAmendBankLimitValidation row's display is
                            // handled separately below via
                            // amendForcedDisplayAmount, since amendPayToLimit
                            // is not guaranteed to be a static preset either
                            // -- this field's own value stays whatever the
                            // real selection/reset-effect already produces.
                            isAmendHardStop
                            ? `${currentMaxAmount}`
                            : `${getAmountRoundUpVer2(
                                accountAgreementInformation[
                                  FORM_ADD_PAYTO_MAX_AMOUNT_NAME
                                ],
                              )}`,
                      }}
                      setInformation={setAccountAgreementInformation}
                      validation={accountAgreementValidation}
                      setValidation={setAccountAgreementValidation}
                      isSearchHidden
                      scrollUp
                      styles={{ marginBottom: 0 }}
                      dropdownStyles={{ height: '44px' }}
                      dropdownMenuContainerStyles={{
                        transform: 'translate(0px, 44px) !important',
                      }}
                      arrowDownIconColor="var(--ds-c-blue-hyperlink-default)"
                      onChange={handleSelectionMaxAmountOnChange}
                      isDisabled={isAmendMaxAmountDisabled}
                      amendForcedValidationMessage={
                        amendForcedValidationMessage
                      }
                      isAmendLimitValidation={isAmendLimitValidation}
                      isAmendBankLimitValidation={isAmendBankLimitValidation}
                      amendForcedDisplayAmount={
                        isAmendBankLimitValidation && !isPaytoMaxAmountEdited
                          ? amendPayToLimit
                          : undefined
                      }
                    />
                  </SectionDesc>
                </BodyContentSection>
                {amendForcedValidationMessage && (
                  <BodyContentSection>
                    <AmendUneditableWarning $isError={isAmendHardStopBankLimit}>
                      {amendForcedValidationMessage}
                    </AmendUneditableWarning>
                  </BodyContentSection>
                )}
                {renderAccountAgreementInfo()}
                <BodyContentSection style={displayFlexRowCenterEndMixinObject}>
                  <DeleteButton onClick={handleOnDelete}>
                    {t('label_delete_agreement')}
                  </DeleteButton>
                </BodyContentSection>
                {/* DEV-1401: hidden with the exact same condition as the
                    Continue button below -- when Continue is hidden
                    (isAmendHideContinueAndNote), this note is no longer
                    relevant. */}
                {!isAmendHideContinueAndNote && (
                  <>
                    <SizeBox height={20} />
                    <BodyContentSection>
                      <SectionDesc
                        style={
                          isDesktop
                            ? fontSize14MixinObject
                            : fontSize12MixinObject
                        }
                      >
                        {t('label_edit_payto_account_popup_note')}
                      </SectionDesc>
                    </BodyContentSection>
                  </>
                )}
              </ModalBody>
              <ModalFooter
                style={{
                  justifyContent: isAmendHideContinueAndNote && 'center',
                }}
              >
                <ButtonCommon
                  value={buttonLabel1}
                  onClick={handleOnClose}
                  styles={{
                    margin: '0px',
                    width: isAmendHideContinueAndNote ? '100%' : '50%',
                  }}
                  color="var(--ds-c-blue)"
                  background="var(--ds-c-white)"
                />
                {!isAmendHideContinueAndNote && (
                  <ButtonCommon
                    value={buttonLabel2}
                    color="var(--c-primary)"
                    background="var(--bg-primary)"
                    isFill={true}
                    styles={{
                      margin: '0px',
                      width: '50%',
                    }}
                    onClick={handleOnSubmit}
                    isUseKeyDown
                  />
                )}
              </ModalFooter>
            </ModalStyled>
          </>
        );
      },
    ),
  ),
);

const ModalStyled = styled(Modal)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0px 16px;
        border-radius: 0px;
        box-shadow: none;
        background: var(--ds-c-white);
        width: 100%;
        height: fit-content;
        margin-inline: auto;
        border: none;
        border-radius: 8px 8px 0 0;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 20px;
        border-radius: 20px;
        box-shadow: var(--ds-bs-4);
        background: var(--ds-c-white);
        width: 476px;
        height: fit-content;
        margin-inline: auto;
        border: none;
      }
    }
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  text-align: left;
  margin-bottom: 16px;
  padding-top: 16px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 20px;
    padding-top: unset;
  }
`;
const BodyContentTop = styled.div`
  ${displayFlexColCenterCenterMixin};
`;
const BodyContentSection = styled.div``;
const Label = styled.label`
  ${paragraphMixin};
  font-size: 16px;
`;
const Desc = styled.p`
  ${paragraphMixin};
  font-size: 20px;
`;
const SectionDesc = styled.p`
  ${paragraphMixin};
  font-size: 14px;
  font-weight: 500;
`;
const AmendUneditableWarning = styled.p`
  ${paragraphMixin};
  font-size: 14px;
  font-weight: 500;
  margin: 8px 0 0;
  /* Last color declaration wins: paragraphMixin already sets
     color: var(--c-primary), so this must stay below the mixin. */
  color: ${(props) =>
    props.$isError ? 'var(--ds-c-red)' : 'var(--c-primary)'};
`;

const ModalFooter = styled(Modal.Footer)`
  display: flex;
  justify-content: space-between;
  margin-inline: -16px;
  padding-inline: 16px;
  padding-block: 16px;
  border-top: 1px solid var(--ds-c-grey-disabled);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding-block: unset;
    padding-inline: unset;
    margin-inline: unset;
    border-top: none;
    justify-content: end;
  }
`;

const DeleteButton = styled.p`
  ${paragraph500Mixin};
  color: #e72631;
  ${cursorPointerMixin};
`;

export default Popup;
